// Pack branches by the occupied card bounds at each depth, rather than reserving
// an entire rectangular column for every subtree. Coordinates are card centers.
export type LayoutPerson = { id: number; levelId: number | null };
export type PositionedPerson = {
  id: number;
  x: number;
  depth: number;
  width: number;
};
export function compactOrganizationLayout<T extends LayoutPerson>(
  roots: T[],
  reports: Map<number, T[]>,
  collapsed: Set<number>,
  sideReportIds: Set<number> = new Set(),
) {
  type Tree = {
    nodes: PositionedPerson[];
    bounds: { left: number; right: number }[];
  };
  function pack(trees: Tree[]): Tree {
    const result: Tree = { nodes: [], bounds: [] };
    for (const tree of trees) {
      let shift = 0;
      tree.bounds.forEach((bound, depth) => {
        const occupied = result.bounds[depth];
        if (occupied) shift = Math.max(shift, occupied.right + 24 - bound.left);
      });
      result.nodes.push(
        ...tree.nodes.map((node) => ({ ...node, x: node.x + shift })),
      );
      tree.bounds.forEach((bound, depth) => {
        const previous = result.bounds[depth];
        result.bounds[depth] = {
          left: Math.min(previous?.left ?? Infinity, bound.left + shift),
          right: Math.max(previous?.right ?? -Infinity, bound.right + shift),
        };
      });
    }
    return result;
  }
  function branch(person: T, ancestors: Set<number>): Tree {
    const width = person.levelId === 1 ? 288 : 240;
    const descendants = collapsed.has(person.id)
      ? []
      : (reports.get(person.id) || []).filter(
          (child) => !ancestors.has(child.id),
        );
    const below = descendants.filter((child) => !sideReportIds.has(child.id));
    const side = descendants.filter((child) => sideReportIds.has(child.id));
    const children = pack(
      below.map((child) => branch(child, new Set([...ancestors, person.id]))),
    );
    const childRoots = children.nodes.filter((node) =>
      below.some((child) => child.id === node.id),
    );
    const center = childRoots.length
      ? (childRoots[0].x + childRoots[childRoots.length - 1].x) / 2
      : 0;
    const result: Tree = {
      nodes: [
        { id: person.id, x: 0, depth: 0, width },
        ...children.nodes.map((node) => ({
          ...node,
          x: node.x - center,
          depth: node.depth + 1,
        })),
      ],
      bounds: [
        { left: -width / 2, right: width / 2 },
        ...children.bounds.map((bound) => ({
          left: bound.left - center,
          right: bound.right - center,
        })),
      ],
    };
    // Direct staff form a separate right-hand branch. Reserve space beyond the
    // entire manager subtree so expanding a manager cannot overlap this column.
    const mainRight = Math.max(...result.bounds.map((bound) => bound.right));
    let sideDepth = 0;
    for (const child of side) {
      const tree = branch(child, new Set([...ancestors, person.id]));
      const left = Math.min(...tree.bounds.map((bound) => bound.left));
      const shift = mainRight + 72 - left;
      result.nodes.push(
        ...tree.nodes.map((node) => ({
          ...node,
          x: node.x + shift,
          depth: node.depth + sideDepth,
        })),
      );
      tree.bounds.forEach((bound, index) => {
        const depth = sideDepth + index;
        const previous = result.bounds[depth];
        result.bounds[depth] = {
          left: Math.min(previous?.left ?? Infinity, bound.left + shift),
          right: Math.max(previous?.right ?? -Infinity, bound.right + shift),
        };
      });
      sideDepth += tree.bounds.length;
    }
    return result;
  }
  const forest = pack(roots.map((person) => branch(person, new Set())));
  const left = Math.min(0, ...forest.bounds.map((bound) => bound.left));
  const right = Math.max(0, ...forest.bounds.map((bound) => bound.right));
  return {
    nodes: forest.nodes.map((node) => ({ ...node, x: node.x - left })),
    width: right - left,
    depth: forest.bounds.length,
  };
}
